import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../../../../components/Input';
import Dropdown from '../../../../../components/Dropdown';
import { VALIDATION_FIELD_MAPPING, STATUS } from '../../../../../utils/appConstant';
import { ListFilters } from '../../../../../interfaces/product.interface';

interface FilterProps {
  status: any[];
  allStatus: any[];
  divisions: any[];
  allDivisions: any[];
  dropdownSearchData: any;
  initialFilters?: ListFilters;
  onFilterChange: (filters: ListFilters) => void;
  onDropdownSearchChange: (e: any, name: string) => void;
}

const Filters: React.FC<FilterProps> = ({
  status,
  allStatus,
  divisions,
  allDivisions,
  dropdownSearchData,
  initialFilters,
  onFilterChange,
  onDropdownSearchChange,
}) => {
  const { t } = useTranslation();

  const [filters, setFilters] = useState<ListFilters>(
    initialFilters || { searchText: '', status: STATUS.active, product_division_id: '' }
  );

  useEffect(() => {
    if (initialFilters) setFilters(initialFilters);
  }, [initialFilters]);

  const handleInputChange = useCallback(
    (event: any, name: string) => {
      setFilters((prev) => ({ ...prev, [name]: event.target.value }));
      if (name !== VALIDATION_FIELD_MAPPING.searchText) {
        onFilterChange({ ...filters, [name]: event.target.value });
      }
    },
    [filters, onFilterChange]
  );

  const handleKeyDown = useCallback(
    (e: any) => { if (e.key === 'Enter') onFilterChange(filters); },
    [filters, onFilterChange]
  );

  const handleOnBlur = useCallback(() => { onFilterChange(filters); }, [filters, onFilterChange]);

  return (
    <div className="row mb-6">
      <div className="col-sm-12">
        <div className="card">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-6 col-sm-12">
                <Input
                  type="search"
                  id={VALIDATION_FIELD_MAPPING.searchText}
                  name={VALIDATION_FIELD_MAPPING.searchText}
                  placeholder={t('products.list.pageTexts.filters.searchPlaceholder')}
                  value={filters.searchText}
                  onBlur={handleOnBlur}
                  onKeyDown={handleKeyDown}
                  onChange={(e) => handleInputChange(e, VALIDATION_FIELD_MAPPING.searchText)}
                />
              </div>
              <div className="col-md-3 col-sm-6">
                <Dropdown
                  values={
                    filters.product_division_id && filters.product_division_id.toString().trim()
                      ? [String(filters.product_division_id)]
                      : []
                  }
                  options={(divisions || []).map((item: any) => ({
                    label: item.name,
                    value: String(item.id),
                  }))}
                  allOptions={(allDivisions || []).map((item: any) => ({
                    label: item.name,
                    value: String(item.id),
                  }))}
                  id={VALIDATION_FIELD_MAPPING.product_division_id}
                  name={VALIDATION_FIELD_MAPPING.product_division_id}
                  searchText={dropdownSearchData.product_division_id}
                  onSearch={onDropdownSearchChange}
                  placeholder={t('products.list.pageTexts.filters.selectDivision')}
                  label={t('products.list.pageTexts.filters.division')}
                  onChange={(e: any) => {
                    const value = Array.isArray(e.target?.value)
                      ? e.target.value[0]
                      : e.target?.value || '';
                    handleInputChange({ target: { value } }, VALIDATION_FIELD_MAPPING.product_division_id);
                  }}
                />
              </div>
              <div className="col-md-3 col-sm-6">
                <Dropdown
                  values={
                    filters.status && filters.status.toString().trim()
                      ? [String(filters.status)]
                      : []
                  }
                  options={(status || []).map((item: any) => ({
                    label: item.name,
                    value: String(item.id),
                  }))}
                  allOptions={(allStatus || []).map((item: any) => ({
                    label: item.name,
                    value: String(item.id),
                  }))}
                  id={VALIDATION_FIELD_MAPPING.status}
                  name={VALIDATION_FIELD_MAPPING.status}
                  searchText={dropdownSearchData.status}
                  onSearch={onDropdownSearchChange}
                  placeholder={t('products.list.pageTexts.filters.selectStatus')}
                  label={t('products.list.pageTexts.filters.status')}
                  onChange={(e: any) => {
                    const value = Array.isArray(e.target?.value)
                      ? e.target.value[0]
                      : e.target?.value || '';
                    handleInputChange({ target: { value } }, VALIDATION_FIELD_MAPPING.status);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Filters;
